import events from "_src/engine/events";
import {Geometry, GeometryBuffer} from "_src/engine/geometry.js";
import intersectEvaluator from "_src/engine/intersect_evaluator";
import {geometry, bufferType} from "_src/engine/constants";
import {toRadian} from "_src/engine/math";

import {vec3, quat} from "_src/libs/gl-matrix";

import renderGraph from "_src/render_graph/render_graph";
import windowManager from "_src/client/window_manager";
import {screenUtils} from "_src/client/utils";
import {gizmoHelpers, Gizmo, setupGizmo, selectedColors, hiddenColors} from "_src/client/widgets/gizmo";

const GIZMO_SCREEN_SIZE = 300;
const DOTTED_LINE_PERIOD = 30;

const TRANSLATE_SENSITIVITY = 1.0;
const ROTATE_SENSITIVITY = 1.0;
const SCALE_SENSITIVITY = 1.0;

const _VEC3_TMP = vec3.create();
const _VEC3_TMP2 = vec3.create();
const _QUAT_TMP = quat.create();

let _loaded = false;

const context = {
  mode       : -1,
  axis       : "",
  angle2d    : 0.0,
  cursorPoint: vec3.create(),
  pickPoint  : vec3.create(),
  startTrans : vec3.create(),
  startRot   : quat.create(),
  startScale : 0.0,
};

const _attachedObjects = [];
const _objectsOffsets = new WeakMap();

export default {

  gizmo : null,

  init() {

    this.gizmo = new Gizmo();

    const mainRenderGraph = windowManager.getWindow(windowManager.MAIN).renderGraph;
    const transformPass = mainRenderGraph.getPass(renderGraph.TRANSFORM);
    const transformToolScene = transformPass.getSecondScene();

    transformToolScene.batcher.sortBlend = true;

    return setupGizmo(this.gizmo).then((loaded) => {

      if (!loaded) {
        return;
      }

      _loaded = true;

      updateGizmoScale(transformToolScene.camera, this.gizmo);

      this.gizmo.addToScene(transformToolScene);

    });

  },

  setPosition(trans) {
    if (!_loaded) {
      return;
    }
    this.gizmo.setTranslation(trans[0], trans[1], trans[2]);
    this.gizmo.updateBoundings();
    events.emit("interface_bounding_changed");
  },

  getPosition() {
    if (!_loaded) {
      return;
    }
    return this.gizmo.getTranslation();
  },

  setRotation(rot) {
    if (!_loaded) {
      return;
    }
    this.gizmo.getRotationConstraint().copyRotation(rot);
    this.gizmo.updateBoundings();
    events.emit("interface_bounding_changed");
  },

  getRotation() {
    if (!_loaded) {
      return;
    }
    return this.gizmo.getRotationConstraint().getRotation();
  },

  updateGizmo(camera) {
    if (!_loaded) {
      return;
    }
    updateGizmoScale(camera, this.gizmo);
    billboardGizmoHelpers(camera, this.gizmo);
    this.gizmo.updateBoundings();
    events.emit("interface_bounding_changed");
  },

  isActive() {
    return context.mode != -1;
  },

  attachObject(object) {
    _attachedObjects.push(object);
  },

  clearAttachedObjects() {
    _attachedObjects.length = 0;
  },

  pick(camera, clientX, clientY) {

    if (!_loaded) {
      return;
    }

    const mainWindow = windowManager.getWindow(windowManager.MAIN);
    const {x, y} = screenUtils.toScreen(mainWindow.getViewport(), camera.viewport, clientX, clientY);

    const { mode, axis } = pickupHoveredComponent(x, y, camera, this.gizmo, context.pickPoint);

    switch (mode) {
      case gizmoHelpers.LOCATION_ARROWS:
      case gizmoHelpers.LOCATION_AXES:
        enableGizmoAxisLocationMode(this.gizmo, context, axis);
        releasePositionWidget(this.gizmo);
        break;
      case gizmoHelpers.SCALE_PLANES:
        break;
      case gizmoHelpers.ROTATION_CURVE:
        enableGizmoRotationMode(this.gizmo, axis);
        break;
      case gizmoHelpers.LOCATION_PLANES:
        enableGizmoPlaneLocationMode(this.gizmo);
        break;
      case gizmoHelpers.LOCATION_DRAG:
        break;
      default:
        return;
    }

    calculate2DCursor(x, y, context, camera, this.gizmo, context.cursorPoint);

    vec3.copy(context.startTrans, this.gizmo.getTranslation());
    quat.copy(context.startRot, this.gizmo.getRotation());

    context.startScale = calculate3DCursorLineLength(x, y, camera, this.gizmo);
    setup2DCursorLine(camera, context, this.gizmo, context.startScale);

    context.mode = mode;
    context.axis = axis;

  },

  startMove(camera, clientX, clientY) {

    if (!_loaded) {
      return;
    }

    const mainWindow = windowManager.getWindow(windowManager.MAIN);
    const {x, y} = screenUtils.toScreen(mainWindow.getViewport(), camera.viewport, clientX, clientY);

    switch (context.mode) {
      case gizmoHelpers.SCALE_PLANES:
        enableGizmoScaleMode(this.gizmo);
        setup2DCursorLine(camera, context, this.gizmo, calculate3DCursorLineLength(x, y, camera, this.gizmo));
        break;
      case gizmoHelpers.ROTATION_CURVE:
        storeObjectsTransform(getObjects());
        break;
    }

  },

  transform(camera, clientX, clientY) {

    if (!_loaded) {
      return;
    }

    const mainWindow = windowManager.getWindow(windowManager.MAIN);
    const {x, y} = screenUtils.toScreen(mainWindow.getViewport(), camera.viewport, clientX, clientY);

    const deltaTrans = vec3.set(_VEC3_TMP, 0, 0, 0);
    const deltaRot = vec3.set(_QUAT_TMP, 0, 0, 0, 1);
    let deltaScale = 0.0;

    calculate2DCursor(x, y, context, camera, this.gizmo, context.cursorPoint);

    switch (context.mode) {
      case gizmoHelpers.LOCATION_ARROWS:
      case gizmoHelpers.LOCATION_AXES:
        calculateTranslation(x, y, context, camera, this.gizmo, deltaTrans);
        break;
      case gizmoHelpers.SCALE_PLANES:
        deltaScale = calculate3DCursorLineLength(x, y, camera, this.gizmo);
        break;
      case gizmoHelpers.ROTATION_CURVE:
        calculateRotation(x, y, context, camera, this.gizmo, deltaRot);
        break;
      case gizmoHelpers.LOCATION_PLANES:
        calculatePlaneTranslation(x, y, context, camera, this.gizmo, deltaTrans);
        break;
      case gizmoHelpers.LOCATION_DRAG:
        calculateDragTranslation(x, y, context, camera, this.gizmo, deltaTrans);
        break;
      default:
        return;
    }

    const objectsDeltaTrans = _VEC3_TMP2;

    // translation
    if (context.mode == gizmoHelpers.LOCATION_ARROWS || context.mode == gizmoHelpers.LOCATION_AXES
        || context.mode == gizmoHelpers.LOCATION_DRAG || context.mode == gizmoHelpers.LOCATION_PLANES) {

      const trans = this.gizmo.getTranslation();
      vec3.copy(objectsDeltaTrans, trans);
      vec3.add(trans, context.startTrans, deltaTrans);
      vec3.sub(objectsDeltaTrans, trans, objectsDeltaTrans);

      this.gizmo.setTranslation(trans[0], trans[1], trans[2]);

      if (context.mode == gizmoHelpers.LOCATION_ARROWS || context.mode == gizmoHelpers.LOCATION_AXES) {
        updatePositionWidget(this.gizmo, context);
      }

      translateObjects(objectsDeltaTrans);

    }

    //rotation
    if (context.mode == gizmoHelpers.ROTATION_CURVE) {

      const rotation = this.gizmo.getRotation();
      quat.multiply(rotation, context.startRot, deltaRot);
      this.gizmo.setRotation(rotation[0], rotation[1], rotation[2], rotation[3]);

      //NOTE: the quat decomposition uses acos inside itself
      deltaRot[3] = Math.max(Math.min(deltaRot[3], 1), -1);
      const angle = quat.getAxisAngle(_VEC3_TMP2, deltaRot);
      const axis = vec3.transformQuat(_VEC3_TMP2, _VEC3_TMP2, rotation);
      quat.setAxisAngle(deltaRot, axis, angle);

      setup2DCursorLine(camera, context, this.gizmo, calculate3DCursorLineLength(x, y, camera, this.gizmo));
      fillRotationIndicator(context, this.gizmo);

      rotateObjects(deltaRot, this.gizmo.getTranslation(), _objectsOffsets);

    }

    //scale
    if (context.mode == gizmoHelpers.SCALE_PLANES) {

      setup2DCursorLine(camera, context, this.gizmo, deltaScale);

      const objectsDeltaScale = 1.0 + SCALE_SENSITIVITY * (deltaScale / context.startScale - 1.0);
      context.startScale = deltaScale;

      scaleObjects(objectsDeltaScale, this.gizmo.getTranslation());

    }

    this.updateGizmo(camera);

  },

  stopMove() {

    if (!_loaded) {
      return;
    }

    const mode = context.mode;
    const axis = context.axis;

    switch (mode) {
      case gizmoHelpers.LOCATION_ARROWS:
      case gizmoHelpers.LOCATION_AXES:
        disableGizmoAxisLocationMode(this.gizmo, axis);
        bindPositionWidget(this.gizmo);
        break;
      case gizmoHelpers.SCALE_PLANES:
        setGizmoScaleSelected(this.gizmo, false);
        disableGizmoScaleMode(this.gizmo);
        break;
      case gizmoHelpers.ROTATION_CURVE:
        disableGizmoRotationMode(this.gizmo);
        break;
      case gizmoHelpers.LOCATION_PLANES:
        disableGizmoPlaneLocationMode(this.gizmo);
        break;
    }

    context.mode = -1;
    context.axis = "";

  },

  highlight(camera, clientX, clientY) {

    if (!_loaded) {
      return;
    }

    const mainWindow = windowManager.getWindow(windowManager.MAIN);
    const {x, y} = screenUtils.toScreen(mainWindow.getViewport(), camera.viewport, clientX, clientY);

    const { mode, axis } = pickupHoveredComponent(x, y, camera, this.gizmo, context.pickPoint);

    switch (mode) {
      case gizmoHelpers.ROTATION_CURVE:
        setCurveHighlight(this.gizmo, axis);
        this.gizmo.getHelper(gizmoHelpers.ROTATION_AXES).showComponent(axis);
        break;
      case gizmoHelpers.LOCATION_ARROWS:
      case gizmoHelpers.LOCATION_AXES:
        setAxisHighlight(this.gizmo, axis);
        break;
      case gizmoHelpers.SCALE_PLANES:
        setGizmoScaleSelected(this.gizmo, true);
        break;
      case gizmoHelpers.LOCATION_DRAG:
        setSelectedGizmoWidget(this.gizmo, true);
        break;
      case gizmoHelpers.LOCATION_PLANES:
        setLocationPlaneMode(this.gizmo, axis);
        break;
    }

  },

  resetHighlight() {

    if (!_loaded) {
      return;
    }

    this.gizmo.getHelper(gizmoHelpers.ROTATION_AXES).hide();
    setSelectedGizmoWidget(this.gizmo, false);
    setGizmoScaleSelected(this.gizmo, false);
    setGizmoDefaultView(this.gizmo);
  },

  cleanup() {
    this.gizmo.cleanup();
  }

}

function updateGizmoScale(camera, gizmo) {

  const scale = screenUtils.calculateObjectScale(GIZMO_SCREEN_SIZE, 2.5,
      gizmo.getTranslation(), camera);

  gizmo.setScale(scale, scale, scale);

}

const billboardGizmoHelpers = (function() {

  const vec3Tmp = vec3.create();

  const quatTmp = quat.create();
  const quatTmp2 = quat.create();
  const quatTmp3 = quat.create();
  const quatTmp4 = quat.create();
  const quatTmp5 = quat.create();
  const quatTmp6 = quat.create();

  return function(camera, gizmo) {

    const toCam = quat.invert(quatTmp, camera.quat);

    const scaleRingsHelper = gizmo.getHelper(gizmoHelpers.SCALE_PLANES);
    if (scaleRingsHelper) {

      const viewQuat = quat.multiply(quatTmp2, toCam, gizmo.getRotation());
      const bbQuat = quat.multiply(quatTmp3, quat.invert(quatTmp3, gizmo.getRotation()), camera.quat);

      const viewAxisX = vec3.transformQuat(vec3Tmp, geometry.AXIS_X, viewQuat);
      const axisXQuat = quat.setAxisAngle(quatTmp4, geometry.AXIS_Z, -Math.atan2(viewAxisX[0], viewAxisX[1]));
      const quatX = quat.multiply(quatTmp4, bbQuat, axisXQuat);

      const viewAxisY = vec3.transformQuat(vec3Tmp, geometry.AXIS_Y, viewQuat);
      const axisYQuat = quat.setAxisAngle(quatTmp5, geometry.AXIS_Z, -Math.atan2(viewAxisY[0], viewAxisY[1]));
      const quatY = quat.multiply(quatTmp5, bbQuat, axisYQuat);

      const localAxisZ = vec3.transformQuat(vec3Tmp, geometry.AXIS_Z, viewQuat);
      const axisZQuat = quat.setAxisAngle(quatTmp6, geometry.AXIS_Z, -Math.atan2(localAxisZ[0], localAxisZ[1]));
      const quatZ = quat.multiply(quatTmp6, bbQuat, axisZQuat);

      scaleRingsHelper.updateSphereBillboard(quatX, quatY, quatZ);

    }

    const gizmoOrigin = gizmo.getOrigin();
    if (gizmoOrigin) {
      gizmoOrigin.copyRotation(camera.quat);
    }

    const cursor = getRotationCursorArrow(gizmo);
    if (cursor) {

      const cursorVec = vec3.sub(vec3Tmp, context.cursorPoint, gizmo.getTranslation());
      vec3.transformQuat(cursorVec, cursorVec, toCam);

      const axisZQuat = quat.setAxisAngle(quatTmp2, geometry.AXIS_Z, -Math.atan2(cursorVec[0], cursorVec[1]));
      const rotation = quat.multiply(quatTmp3, camera.quat, axisZQuat);

      cursor.copyRotation(rotation);

    }

  }

})();

const pickupHoveredComponent = (function() {

  const from = vec3.create();
  const to = vec3.create();
  const point = vec3.create();

  return function(x, y, camera, gizmo, dest) {

    const origin = camera.calculateOrigin(x, y, from);
    const ray = camera.calculateRay(x, y, to);

    let mode = "";
    let axis = "";
    let visFactor = Infinity;

    for (let helperid in gizmoHelpers) {

      if (gizmoHelpers[helperid] == gizmoHelpers.LOCATION_DRAG) {
        if (getGizmoWidget(gizmo).castRay(origin, ray, point)) {
          visFactor = vec3.distance(origin, point);
          vec3.copy(dest, point);
          mode = gizmoHelpers.LOCATION_DRAG;
        }
      } else {

        const helper = gizmo.getHelper(gizmoHelpers[helperid]);

        for (let component in helper.components) {
          if (helper.castRay(component, origin, ray, point)) {
            const factor = vec3.distance(origin, point);
            if (factor < visFactor) {
              vec3.copy(dest, point);
              mode = gizmoHelpers[helperid];
              axis = component;
              visFactor = factor;
            }
          }
        }

      }

    }

    return { mode, axis };

  }

})();

function getGizmoWidget(gizmo) {
  return gizmo.widget;
}

function enableGizmoAxisLocationMode(gizmo, scope, axis) {

  const helper = gizmo.getHelper(gizmoHelpers.LOCATION_AXES);
  const component = helper.components[axis];
  component.setScale(1, 1000, 1);

  switch(axis) {
    case "X":
      component.setTranslation(-500, 0, 0);
      break;
    case "Y":
      component.setTranslation(0, -500, 0);
      break;
    case "Z":
      component.setTranslation(0, 0, -500);
      break;
  }

  hideAllHelpers(gizmo);

  helper.show();
  helper.drawOneComponent(axis);

}

function hideAllHelpers(gizmo) {
  for (let helperid in gizmoHelpers) {
    const helper = gizmo.getHelper(gizmoHelpers[helperid]);
    if (helper) {
      helper.hide();
    }
  }
  getRotationCursorLine(gizmo).hide();
  getRotationCursorArrow(gizmo).hide();
}

function getRotationCursorLine(gizmo) {
  return gizmo.cursor;
}

function getRotationCursorArrow(gizmo) {
  return gizmo.cursorArrow;
}

function releasePositionWidget(gizmo) {

  const view = gizmo.getOrigin();
  const widget = getGizmoWidget(gizmo);
  const scale = gizmo.getScaleConstraint().getScale()[0];

  widget.copyRotation(view.quat);
  widget.setScale(scale, scale, scale);

  view.remove(widget);
  gizmo.getLocationConstraint().add(widget);

}

function enableGizmoRotationMode(gizmo, axis) {

  hideAllHelpers(gizmo);
  getGizmoWidget(gizmo).hide();

  getRotationCursorLine(gizmo).show();
  getRotationCursorArrow(gizmo).show();

  const rotationHelper = gizmo.getHelper(gizmoHelpers.ROTATION_AXES);
  const indicatorHelper = gizmo.getHelper(gizmoHelpers.ROTATION_INDICATOR);

  indicatorHelper.show();
  rotationHelper.show();

  rotationHelper.drawOneComponent(axis);

  for (let component in indicatorHelper.components) {
    if (component == axis) {
      indicatorHelper.components[component].getChildren()[0].show();
    }
  }

  getRotationCursorArrow(gizmo).textures[0] = getRotationCursorArrow(gizmo).textures[2];

}

function enableGizmoPlaneLocationMode(gizmo) {
  hideAllHelpers(gizmo);
}

function disableGizmoPlaneLocationMode(gizmo) {
  showAllHelpers(gizmo);
}

const calculate2DCursor = (function() {

  const from = vec3.create();
  const to = vec3.create();
  const norm = vec3.create();
  const point = vec3.create();

  return function(x, y, scope, camera, gizmo, dest) {

    camera.calculateOrigin(x, y, from);
    camera.calculateRay(x, y, to);

    vec3.set(norm, 0, 0, -1);
    vec3.transformQuat(norm, norm, camera.quat);

    if (intersectEvaluator.linePlaneTest(norm, gizmo.getTranslation(), from, to, point)) {
      vec3.copy(dest, point);
    }

    return dest;

  }

})();

const calculate3DCursorLineLength = (function() {

  const from = vec3.create();
  const to = vec3.create();
  const norm = vec3.create();
  const point = vec3.create();

  return function(x, y, camera, gizmo) {

    camera.calculateOrigin(x, y, from);
    camera.calculateRay(x, y, to);

    vec3.set(norm, 0, 0, -1);
    vec3.transformQuat(norm, norm, camera.quat);

    if (intersectEvaluator.linePlaneTest(norm, gizmo.getTranslation(), from, to, point)) {
      return vec3.distance(gizmo.getTranslation(), point);
    }

    return 1.0;

  }

})();

const setup2DCursorLine = (function() {

  const vec3Tmp = vec3.create();

  const quatTmp = quat.create();

  const pixelRatio = screenUtils.getPixelRatio();

  return function(camera, scope, gizmo, scale) {

    const cursorVec = vec3.sub(vec3Tmp, scope.cursorPoint, gizmo.getTranslation());
    const toCam = quat.invert(quatTmp, camera.quat);
    vec3.transformQuat(cursorVec, cursorVec, toCam);

    const line = getRotationCursorLine(gizmo);
    const arrow = getRotationCursorArrow(gizmo);

    arrow.copyTranslation(scope.cursorPoint);
    vec3.sub(arrow.trans, arrow.trans, gizmo.getTranslation());

    const angle = -Math.atan2(cursorVec[0], cursorVec[1]);
    const zQuat = quat.setAxisAngle(quatTmp, geometry.AXIS_Z, angle);
    const rotation = quat.multiply(quatTmp, camera.quat, zQuat);

    line.copyRotation(rotation);
    line.setScale(1, scale, 1);

    const mainWindow = windowManager.getWindow(windowManager.MAIN);
    const distToCamera = vec3.distance(camera.trans, gizmo.getTranslation());

    const viewport = mainWindow.getViewport();
    const width = Math.max(viewport[2] - viewport[0], viewport[3] - viewport[1]);

    line.shader.setTexturePeriod(pixelRatio * DOTTED_LINE_PERIOD / width / scale * distToCamera);

  }

})();

function enableGizmoScaleMode(gizmo) {

  hideAllHelpers(gizmo);
  getGizmoWidget(gizmo).hide();
  getRotationCursorLine(gizmo).show();
  getRotationCursorArrow(gizmo).show();

  getRotationCursorArrow(gizmo).textures[0] = getRotationCursorArrow(gizmo).textures[1];

}

function disableGizmoAxisLocationMode(gizmo, axis) {

  const component = gizmo.getHelper(gizmoHelpers.LOCATION_AXES).components[axis];
  component.setScale(1, 1, 1);
  component.setTranslation(0, 0, 0);

  showAllHelpers(gizmo);

}

function disableGizmoScaleMode(gizmo) {

  showAllHelpers(gizmo);
  getGizmoWidget(gizmo).show();

}

function setGizmoScaleSelected(gizmo, selected) {

  const helper = gizmo.getHelper(gizmoHelpers.SCALE_PLANES);

  const opacity = selected ? 1.0 : 0.3;
  helper.setOpacity(opacity);

  for (let axis in helper.components) {

    const component = helper.components[axis];
    const uTexture = component.textures[1];
    const sTexture = component.textures[2];

    component.textures[0] = selected ? sTexture : uTexture;

  }

}

function setSelectedGizmoWidget(gizmo, selected) {

  const widget = getGizmoWidget(gizmo);

  const textureNum = selected ? 2 : 1;

  widget.textures[0] = widget.textures[textureNum];

}

function disableGizmoRotationMode(gizmo) {

  showAllHelpers(gizmo);
  getGizmoWidget(gizmo).show();
  getRotationCursorLine(gizmo).hide();
  getRotationCursorArrow(gizmo).hide();

  const indicatorHelper = gizmo.getHelper(gizmoHelpers.ROTATION_INDICATOR);
  indicatorHelper.cleanup();

  for (let component in indicatorHelper.components) {
    indicatorHelper.components[component].getChildren()[0].hide();
  }

}

function showAllHelpers(gizmo) {
  for (let helperid in gizmoHelpers) {
    const helper = gizmo.getHelper(gizmoHelpers[helperid]);
    if (helper) {
      helper.show();
    }
  }
  gizmo.getHelper(gizmoHelpers.ROTATION_AXES).hide();
  getRotationCursorLine(gizmo).hide();
  getRotationCursorArrow(gizmo).hide();
}

function initRotationIndicator(primitive) {

  const positions = new Float32Array([0, 0, 0]);
  const positionsItem = new GeometryBuffer({ buffer : positions.buffer, type : bufferType.F32_3 });

  const indices = new Uint32Array(3);
  const indicesItem = new GeometryBuffer({ buffer : indices.buffer, type : bufferType.UI32_3 });

  const layout = {
    "a_position" : positionsItem,
    "indices": indicesItem
  };

  const geometry = new Geometry({
    bbox     : [0,0,0,0,0,0],
    layout   : layout,
  });

  primitive.setChunkGeometry(primitive.chunks[0], geometry);

}

function updateRotationIndicator(primitive, x, y) {

  const chunk = primitive.chunks[0];
  const geometry = chunk.geometry;

  const positionsItem = geometry.getLayoutItem("a_position");
  const indicesItem = geometry.getLayoutItem("indices");

  const positions = positionsItem.getBuffer();
  const indices = indicesItem.getBuffer();

  const newPositions = new Float32Array(positions.length + 3);
  const newIndices = new Uint32Array(indices.length + 1);

  newPositions.set(positions);
  newIndices.set(indices);

  const newIndex = positions.length / 3;

  newPositions[positions.length] = x;
  newPositions[positions.length + 1] = y;

  newIndices[indices.length] = newIndex;

  positionsItem.setBuffer(newPositions.buffer);
  indicesItem.setBuffer(newIndices.buffer);

  chunk.markAttributeDirty("a_position");
  chunk.markAttributeDirty("indices");

}

const fillRotationIndicator = (function() {

  const zero = vec3.create();
  const srartDirection = vec3.create();

  const offset = quat.create();

  let prevAngle = 0;

  return function(scope, gizmo) {

    let direction = 1;

    if (scope.axis == "X") {
      vec3.set(zero, 0, 0, -1);
      quat.setAxisAngle(offset, geometry.AXIS_Y, 0.5 * Math.PI);
    }

    if (scope.axis == "Y") {
      vec3.set(zero, 1, 0, 0);
      quat.setAxisAngle(offset, geometry.AXIS_X, -0.5 * Math.PI);
      direction = -1;
    }

    if (scope.axis == "Z") {
      vec3.set(zero, 1, 0, 0);
      quat.setAxisAngle(offset, geometry.AXIS_Z, 0);
    }

    const sd = vec3.sub(srartDirection, scope.pickPoint, gizmo.getTranslation());
    const length = vec3.length(sd);

    if (length == 0) {
      return;
    }

    vec3.scale(srartDirection, srartDirection, 1.0 / length);
    const zd = vec3.transformQuat(zero, zero, scope.startRot);

    const angle2d = scope.angle2d > 0 ? scope.angle2d : 2 * Math.PI + scope.angle2d;
    const startAngle = direction * Math.acos(vec3.dot(sd, zd));
    const angle = angle2d + startAngle;

    const primitive = gizmo.getHelper(gizmoHelpers.ROTATION_INDICATOR).components[scope.axis];
    const chunk = primitive.chunks[0];

    if (chunk.isEmpty()) {
      prevAngle = 2 * Math.PI + startAngle;
    }

    let deltaAngle = angle - prevAngle;

    if (deltaAngle == 0) {
      return;
    }

    let isNewCircle = false;

    if (deltaAngle > 0) {
      isNewCircle = deltaAngle > Math.PI && angle2d > 1.5 * Math.PI;
      prevAngle = isNewCircle ? 2 * Math.PI + startAngle : prevAngle;
    } else {
      isNewCircle = deltaAngle < -Math.PI && angle2d < 0.5 * Math.PI;
      prevAngle = isNewCircle ? startAngle : prevAngle;
    }

    if (isNewCircle) {
      chunk.cleanup();
      deltaAngle = angle - prevAngle;
    }

    if (chunk.isEmpty()) {
      initRotationIndicator(primitive);
    }

    const numStep = Math.abs(Math.floor(deltaAngle / toRadian(1.0)));
    const step = deltaAngle / numStep;

    for (let i = 0; i < numStep; ++i) {
      addRotationIndicatorPoint(primitive, prevAngle + step * i);
    }

    addRotationIndicatorPoint(primitive, angle);

    prevAngle = angle;

    const rotation = quat.setAxisAngle(primitive.getRotation(), geometry.AXIS_Z, -scope.angle2d);
    quat.multiply(rotation, offset, rotation);
    primitive.copyRotation(rotation);

  }

})();

function addRotationIndicatorPoint(primitive, angle) {

  const r = 0.62;
  const x = r * Math.cos(angle);
  const y = r * Math.sin(angle);

  updateRotationIndicator(primitive, x, y);

}

function updatePositionWidget(gizmo, scope) {

  const widget = getGizmoWidget(gizmo);

  vec3.sub(widget.trans, gizmo.getTranslation(), scope.startTrans);
  vec3.scale(widget.trans, widget.trans, -1);

  widget.copyTranslation(widget.trans);

}

function bindPositionWidget(gizmo) {

  const widget = getGizmoWidget(gizmo);

  widget.copyTranslation(vec3.create());
  widget.copyRotation(quat.create());
  widget.setScale(1, 1, 1);

  gizmo.getLocationConstraint().remove(widget);
  gizmo.getOrigin().add(widget);

}

const calculateTranslation = (function() {

  const from = vec3.create();
  const to = vec3.create();
  const axis = vec3.create();
  const viewAxis = vec3.create();
  const view = vec3.create();
  const point = vec3.create();
  const direction = vec3.create();

  return function(x, y, scope, camera, gizmo, dest) {

    camera.calculateOrigin(x, y, from);
    camera.calculateRay(x, y, to);

    vec3.set(axis, 0, 0, 0);
    vec3.set(view, 0, 0, -1);

    if (scope.axis == "X") axis[0] = 1;
    if (scope.axis == "Y") axis[1] = 1;
    if (scope.axis == "Z") axis[2] = 1;

    vec3.transformQuat(axis, axis, gizmo.getRotation());
    vec3.transformQuat(viewAxis, axis, camera.quat);

    vec3.transformQuat(view, view, camera.quat);

    if (intersectEvaluator.linePlaneTest(view, scope.pickPoint, from, to, point)) {

      vec3.sub(direction, point, scope.pickPoint);
      vec3.transformQuat(direction, direction, camera.quat);

      const distance = vec3.dot(direction, viewAxis);
      vec3.scale(dest, axis, distance * TRANSLATE_SENSITIVITY);

    }

    return dest;

  }

})();

const calculateRotation = (function() {

  const from = vec3.create();
  const to = vec3.create();
  const axis = vec3.create();
  const view = vec3.create();
  const point = vec3.create();
  const vec3Tmp = vec3.create();
  const vec3Tmp2 = vec3.create();
  const vec3Tmp3 = vec3.create();

  const toCam = quat.create();

  return function(x, y, scope, camera, gizmo, dest) {

    camera.calculateOrigin(x, y, from);
    camera.calculateRay(x, y, to);

    vec3.set(view, 0, 0, -1);
    vec3.transformQuat(view, view, camera.quat);

    if (intersectEvaluator.linePlaneTest(view, scope.pickPoint, from, to, point)) {

      const cursorVec = vec3.sub(vec3Tmp, point, gizmo.getTranslation());
      const startVec = vec3.sub(vec3Tmp2, scope.pickPoint, gizmo.getTranslation());

      quat.invert(toCam, camera.quat);
      vec3.transformQuat(cursorVec, cursorVec, toCam);
      vec3.transformQuat(startVec, startVec, toCam);

      const startAngle = Math.PI + Math.atan2(startVec[0], startVec[1]);

      const x = Math.cos(startAngle) * cursorVec[0] - Math.sin(startAngle) * cursorVec[1];
      const y = Math.sin(startAngle) * cursorVec[0] + Math.cos(startAngle) * cursorVec[1];
      const angle = Math.PI - Math.atan2(x, y);

      vec3.set(axis, 0, 0, 0);

      if (scope.axis == "X") axis[0] = 1;
      if (scope.axis == "Y") axis[1] = 1;
      if (scope.axis == "Z") axis[2] = 1;

      const rotSign = Math.sign(-vec3.dot(view, vec3.transformQuat(vec3Tmp3, axis, gizmo.getRotation()))) || 1;
      vec3.scale(axis, axis, rotSign);

      scope.angle2d = rotSign * ROTATE_SENSITIVITY * angle;

      quat.setAxisAngle(dest, axis, ROTATE_SENSITIVITY * angle);

    }

  }

})();

function setLocationPlaneMode(gizmo, axis) {

  const red = axis == "X" ? selectedColors.red : hiddenColors.red;
  const green = axis == "Y" ? selectedColors.green : hiddenColors.green;
  const blue = axis == "Z" ? selectedColors.blue : hiddenColors.blue;

  gizmo.getHelper(gizmoHelpers.LOCATION_PLANES).setComponentsColor(red, green, blue);

  gizmo.getHelper(gizmoHelpers.LOCATION_ARROWS).setComponentsColor(hiddenColors.red,
      hiddenColors.green, hiddenColors.blue);
  gizmo.getHelper(gizmoHelpers.LOCATION_AXES).setComponentsColor(hiddenColors.red,
      hiddenColors.green, hiddenColors.blue);
  gizmo.getHelper(gizmoHelpers.ROTATION_CURVE).setComponentsColor(hiddenColors.red,
      hiddenColors.green, hiddenColors.blue);

}

function setAxisHighlight(gizmo, axis) {

  const red = axis == "X" ? selectedColors.red : hiddenColors.red;
  const green = axis == "Y" ? selectedColors.green : hiddenColors.green;
  const blue = axis == "Z" ? selectedColors.blue : hiddenColors.blue;

  gizmo.getHelper(gizmoHelpers.LOCATION_ARROWS).setComponentsColor(red, green, blue);
  gizmo.getHelper(gizmoHelpers.LOCATION_AXES).setComponentsColor(red, green, blue);

  gizmo.getHelper(gizmoHelpers.ROTATION_CURVE).setComponentsColor(hiddenColors.red,
      hiddenColors.green, hiddenColors.blue);
  gizmo.getHelper(gizmoHelpers.LOCATION_PLANES).setComponentsColor(hiddenColors.red,
      hiddenColors.green, hiddenColors.blue);

}

function setCurveHighlight(gizmo, axis) {

  const red = axis == "X" ? selectedColors.red : hiddenColors.red;
  const green = axis == "Y" ? selectedColors.green : hiddenColors.green;
  const blue = axis == "Z" ? selectedColors.blue : hiddenColors.blue;

  gizmo.getHelper(gizmoHelpers.ROTATION_CURVE).setComponentsColor(red, green, blue);

  gizmo.getHelper(gizmoHelpers.LOCATION_ARROWS).setComponentsColor(hiddenColors.red,
      hiddenColors.green, hiddenColors.blue);
  gizmo.getHelper(gizmoHelpers.LOCATION_AXES).setComponentsColor(hiddenColors.red,
      hiddenColors.green, hiddenColors.blue);
  gizmo.getHelper(gizmoHelpers.LOCATION_PLANES).setComponentsColor(hiddenColors.red,
      hiddenColors.green, hiddenColors.blue);

}

function setGizmoDefaultView(gizmo) {

  gizmo.getHelper(gizmoHelpers.SCALE_PLANES).setOpacity(0.3);

  gizmo.getHelper(gizmoHelpers.LOCATION_AXES).setComponentsColor(selectedColors.red,
      selectedColors.green, selectedColors.blue);
  gizmo.getHelper(gizmoHelpers.LOCATION_ARROWS).setComponentsColor(selectedColors.red,
      selectedColors.green, selectedColors.blue);
  gizmo.getHelper(gizmoHelpers.ROTATION_CURVE).setComponentsColor(selectedColors.red,
      selectedColors.green, selectedColors.blue);
  gizmo.getHelper(gizmoHelpers.LOCATION_PLANES).setComponentsColor(selectedColors.red,
      selectedColors.green, selectedColors.blue);

}

const calculatePlaneTranslation = (function() {

  const from = vec3.create();
  const to = vec3.create();
  const axis = vec3.create();
  const point = vec3.create();

  return function(x, y, scope, camera, gizmo, dest) {

    camera.calculateOrigin(x, y, from);
    camera.calculateRay(x, y, to);

    vec3.set(axis, 0, 0, 0);

    if (scope.axis == "X") axis[0] = 1;
    if (scope.axis == "Y") axis[1] = 1;
    if (scope.axis == "Z") axis[2] = 1;

    vec3.transformQuat(axis, axis, gizmo.getRotation());

    if (intersectEvaluator.linePlaneTest(axis, scope.pickPoint, from, to, point)) {
      vec3.sub(dest, point, scope.pickPoint)
    }

    return dest;

  }

})();

const calculateDragTranslation = (function() {

  const from = vec3.create();
  const to = vec3.create();
  const norm = vec3.create();
  const point = vec3.create();

  return function(x, y, scope, camera, gizmo, dest) {

    camera.calculateOrigin(x, y, from);
    camera.calculateRay(x, y, to);

    vec3.set(norm, 0, 0, -1);
    vec3.transformQuat(norm, norm, camera.quat);

    if (intersectEvaluator.linePlaneTest(norm, scope.pickPoint, from, to, point)) {
      vec3.sub(dest, point, scope.pickPoint)
    }

    return dest;

  }

})();

function storeObjectsTransform(models) {

  for (let model of models) {
    _objectsOffsets.set(model, {
      trans : vec3.clone(model.trans),
      quat  : quat.clone(model.quat)
    });
  }

}

const translateObjects = (function() {

  return function (deltaTrans) {

    const models = getObjects();

    for (let model of models) {

      const trans = model.getTranslation();
      model.copyTranslation(vec3.add(trans, trans, deltaTrans));

      events.emit("model_transformed", model);

    }

  }

})();

const rotateObjects = (function() {

  const toCenter = vec3.create();
  const toCenterRot = vec3.create();

  return function (deltaRot, around, storage) {

    const models = getObjects();

    for (let model of models) {

      const trans0 = storage.get(model).trans;
      const rotation0 = storage.get(model).quat;

      const trans = model.getTranslation();
      const rotation = model.getRotation();

      vec3.sub(toCenter, around, trans0);
      vec3.transformQuat(toCenterRot, toCenter, deltaRot);
      vec3.sub(toCenter, toCenter, toCenterRot);
      vec3.add(trans, trans0, toCenter);

      quat.multiply(rotation, deltaRot, rotation0);

      model.copyTranslation(trans);
      model.copyRotation(rotation);

      events.emit("model_transformed", model);

    }

  }

})();

const scaleObjects = (function() {

  const delta = vec3.create();

  return function (deltaScale, from) {

    const models = getObjects();

    for (let model of models) {

      const scale = model.getScale();
      const trans = model.getTranslation();

      vec3.sub(delta, from, trans);
      vec3.scale(delta, delta, deltaScale - 1.0);
      vec3.sub(trans, trans, delta);

      model.copyTranslation(trans);
      model.copyScale(vec3.scale(scale, scale, deltaScale));

      events.emit("model_transformed", model);

    }

  }

})();

function getObjects() {
  return _attachedObjects;
}
